import { Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import { useTheme } from "design-system/themeContext";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useGetBuilding, useGetBuildingFiles } from "../api/hooks/allHooks";
import { AssetDetailedCard } from "../components/BuildingProfile/AssetDetailedCard";
import { BuildingEvents } from "../components/BuildingProfile/BuildingEvents";
import { BuildingProfileFiles } from "../components/BuildingProfile/BuildingProfileFiles";
import { EPCPart } from "../components/BuildingProfile/EPCPart";
import { ESGIndex } from "../components/BuildingProfile/ESGIndex";
import { KPIAndStrandingPart } from "../components/BuildingProfile/KPIAndStrandingPart";
import { UseTypesHorizontal } from "../components/BuildingProfile/UseTypesHorizontal";
import { CaptureComponent } from "../components/CaptureComponent";
import { NavigationTabs } from "../components/NavigationTabs";
import ScrollToTop from "../components/ScrollToTop";
import { WizardContextProvider } from "../context/Wizard";
import { eventNames, trackGAEvent } from "../functions/googleAnalytics";

export const BuildingProfile = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const theme = useTheme();
  const { data: dataBuilding, isLoading } = useGetBuilding(projectId);
  const building = dataBuilding || {};

  const { data: buildingFiles, isLoading: bFIsLoading } = useGetBuildingFiles(projectId);

  useEffect(() => {
    trackGAEvent(eventNames.BP_VIEW);
  }, []);

  const assetCardConfig = [
    [
      { dataKey: "descriptive_name", label: "ASSET NAME" },
      { dataKey: "type", label: "TYPE" },
      { dataKey: "asset_id", label: "ASSET ID" },
      { dataKey: "address", label: "ADDRESS" },
    ],
    [
      { dataKey: "construction_year", label: "YEAR OF CONSTRUCTION" },
      { dataKey: "last_modernized", label: "MODERNIZATION YEAR" },
      {
        dataKey: "gross_asset_basement_included",
        isNumeric: true,
        label: "GROSS AREA (INCL. BASEMENT)",
        renderUnit: (value) => value && " m²",
      },
      {
        dataKey: "net_lettable_area_without_basement",
        isNumeric: true,
        label: "Net LETTABLE AREA (WITHOUT BASEMENT)",
        renderUnit: (value) => value && " m²",
      },
    ],
  ];
  const containerRef = useRef();

  const epcCrremSections = [{ name: "epc" }, { name: "crrem" }, { name: "esg" }];
  const [epcCrremSelectedSection, setEpcCrremSelectedSection] = useState(epcCrremSections[0]);

  const containerRef2 = useRef();

  return (
    <>
      <Helmet>
        <title>Building profile | {theme.clientDisplayName}</title>
      </Helmet>
      <ScrollToTop />
      <Stack className="items-stretch justify-between" ref={containerRef2} spacing={6}>
        {/* Left column */}
        <Paper className=" min-w-[47%] max-w-[50%] overflow-hidden">
          <AssetDetailedCard
            building={building}
            buildingType={building?.type}
            className="border-none bg-transparent p-0"
            config={assetCardConfig}
            imgPath={building?.image_url_value}
            isLoading={isLoading}
            name={t("BuildingProfile.card.title")}
          />
          <UseTypesHorizontal className="mt-8 border-none bg-transparent p-0" projectId={projectId} />
        </Paper>
        {/* Right column */}
        <section className=" w-[49%]">
          <div className="h-full" ref={containerRef}>
            <Paper className="relative h-full">
              <CaptureComponent
                className="absolute right-6 top-6"
                size="md"
                targetElement={() => containerRef.current}
              />
              <NavigationTabs
                activeTab={epcCrremSelectedSection}
                setActiveTab={setEpcCrremSelectedSection}
                tabs={epcCrremSections}
              />
              {epcCrremSelectedSection.name === "epc" && <EPCPart projectId={projectId} />}
              {epcCrremSelectedSection.name === "crrem" && <KPIAndStrandingPart projectId={projectId} />}
              {epcCrremSelectedSection.name === "esg" && <ESGIndex projectId={projectId} />}
            </Paper>
          </div>
        </section>
      </Stack>
      <WizardContextProvider defaultQuantity="consumption" projectId={projectId}>
        <BuildingEvents className="mt-8" projectId={projectId} />
      </WizardContextProvider>
      <BuildingProfileFiles
        buildingFiles={buildingFiles || []}
        className="mt-8 p-6"
        isLoading={bFIsLoading}
        projectId={projectId}
        tableClassName="max-h-[30rem]"
      />
    </>
  );
};
